import get from 'lodash/get';
import React, { useEffect, useState, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';

import { SingleSelect } from '@els/els-react--select';
import HoverTooltip from '@Components/HoverTooltip';
import { TooltipConstants } from '@Components/Tooltip';
import Button from '@Components/Button';
import AutosuggestInput from '@Components/AutosuggestInput';
import { TAXONOMY_BOUND_OPERATORS } from '../constants';

import './TaxonomyLookupModalHeader.custom.scss';
import './TaxonomyLookupModalHeader.theme.scss';

function TaxonomyLookupModalHeader({
  opModel,
  search,
  getSuggestions,
  boundOperator,
  searchByKeyword,
  selectBoundOperator,
  totalHitCount,
  suggestInit,
}) {
  const inputRef = useRef();
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (search === searchValue) {
      return;
    }
    if (!search && search !== '') {
      return;
    }

    setSearchValue(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearchSelect = (item) => {
    setSearchValue(item.value);
    const ref = get(inputRef, 'current');
    if (ref) {
      ref.focus();
    }
  };

  const operatorsDropdownOptions = Object.keys(TAXONOMY_BOUND_OPERATORS).map((operator) => ({
    label: TAXONOMY_BOUND_OPERATORS[operator],
    value: operator,
  }));

  const handleSearch = () => {
    searchByKeyword(searchValue);
  };

  const handleInputClear = () => {
    const ref = get(inputRef, 'current');
    if (ref) {
      ref.blur();
    }
    searchByKeyword('');
  };

  const handleBoundOperatorSelect = ({ value }) => {
    selectBoundOperator(value);
  };

  return (
    <div className="taxonomyLookupModal__header">
      <div className="taxonomyLookupModal__header_wrapper taxonomyLookupModal__header_wrapper-main">
        <HoverTooltip
          id={TooltipConstants.ids.GlobalHover}
          className="taxonomyLookupModal__header_tooltipContainer"
          tooltip={ReactDOMServer.renderToString(<div className="taxonomyLookupModal__header_tooltip">{opModel.title}</div>)}
          place="bottom">
          <h4 className="rx-newmodal-title taxonomyLookupModal__header_title">{opModel.title}</h4>
          {!!totalHitCount && <div className="taxonomyLookupModal__header_badge rx-accordion-header-badge">{totalHitCount}</div>}
        </HoverTooltip>
      </div>

      <div className="taxonomyLookupModal__header_wrapper">
        <div className="taxonomyLookupModal__header_dropdown">
          <SingleSelect
            id="taxonomyLookupModal__header_dropdown"
            className="e2e-taxonomyLookupModal-header-dropdown"
            options={operatorsDropdownOptions}
            selectedOptions={[boundOperator]}
            label="Select bound operator"
            required
            onSelect={handleBoundOperatorSelect}
            size="small"
            shouldHideLabel
          />
        </div>
      </div>

      <div className="taxonomyLookupModal__header_wrapper taxonomyLookupModal__header_wrapper-main">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className="taxonomyLookupModal__header_input">
          <Button
            onClick={() => searchByKeyword(searchValue)}
            type="button"
            buttonConfig={{
              type: 'link',
              size: 'sm',
              ariaLabel: 'TaxonomyLookup modal search',
              iconOnly: true,
              e2eName: 'taxonomyLookup-modal-header-search',
              className: 'taxonomyLookupModal__header_input_button',
            }}
            iconConfig={{
              className: 'taxonomyLookupModal__header_input_icon',
              name: 'search',
              size: 'sm',
            }}
          />

          <AutosuggestInput
            autofocus={!!suggestInit.search}
            animatedPlaceholder={false}
            placeholder="Enter search term"
            getData={getSuggestions}
            idInput="taxonomyLookupModal__header_input"
            classNameInput="e2e-taxonomyLookupModal-header-input"
            value={searchValue}
            onChange={setSearchValue}
            onSelect={handleSearchSelect}
            onClear={handleInputClear}
            debounceDelay={500}
            groupBy="category"
            highlight
            minSymbolsToStart={1}
            inputProps={{ ref: inputRef }}
            onSearch={handleSearch}
          />
        </div>
      </div>
    </div>
  );
}

TaxonomyLookupModalHeader.propTypes = {
  opModel: PropTypes.object,
  suggestInit: PropTypes.object,
  boundOperator: PropTypes.string,
  search: PropTypes.string,
  getSuggestions: PropTypes.func.isRequired,
  searchByKeyword: PropTypes.func.isRequired,
  selectBoundOperator: PropTypes.func.isRequired,
  totalHitCount: PropTypes.number,
};

export default TaxonomyLookupModalHeader;
